<script>
  import { _ } from "svelte-i18n"
  import { onMount, onDestroy } from 'svelte'
  import { flash_content, flash_shown_copy_paste, text_entered, local_fonts, user_valid, previews_are_google_fonts, dropped_font_files } from '../stores'
  import { decode } from 'he'
  import { sampleSize } from 'lodash'
  import opentype from 'opentype.js'
  import HorizontalOptionsList from '../elements/HorizontalOptionsList.svelte'
  import UAParser from 'ua-parser-js'
  import Insert from "../elements/Insert.svelte"
  import OpenBuyModalButton from "./OpenBuyModalButton.svelte";
  import Blink from "../elements/Blink.svelte";
  import { readFontFileData } from '../services/font_files';
  
  export let font_obj

  let font
  let all_glyphs = []
  let alternative_glyphs = []
  let is_visible = false
  let element
  let view_option = "none"
  let copyToClipboardSuccess = false
  let glyphs_loaded = $previews_are_google_fonts || !!$local_fonts ? false : true;
  
  const uaParser = new UAParser()
  const windows = uaParser.getOS().name === 'Windows'
  const keys = windows ? 'CTRL + V' : 'command + V'
  const supports_local_font_access = 'queryLocalFonts' in self


  $: view_options = view_option === "none" 
    ? [
      {value:'all', display:'Show all characters'}, 
      {value:'alternative', display:'Guess alternative characters'}, 
      ]
    : [
      {value:'all', display:'Show all characters'}, 
      {value:'alternative', display:'Guess alternative characters'}, 
      {value:'none', display:'Hide all'}
      ];
  $: view_option && toggleCharacterMap()
  $: glyphs_to_show = view_option === "all" ? all_glyphs : view_option === "alternative" ? alternative_glyphs : []

  onMount(() => {
    getFontSFNT()
  })

  async function getFontSFNT(skip=false) {
    
    if (font_obj.source === 'dropped') {
      const file = $dropped_font_files.find(file => file.name === font_obj.file_name);
      const reader = new FileReader();
      reader.onload = function(e) {
        const arrayBuffer = e.target.result;
        font = opentype.parse(arrayBuffer);
        console.log("GlyphsViewer - loaded dropped font")
        getAllGlyphs()
      }
      reader.readAsArrayBuffer(file);
    }

    else if (font_obj.source === 'google') {
      console.log("load urL: "+ font_obj.url)
      font = await opentype.load(font_obj.url)
      console.log("GlyphsViewer - loaded Google font")
      getAllGlyphs()
    } 

    else if (font_obj.source === 'uploaded') {
      try {
        const file = await readFontFileData(font_obj.file_name, font_obj.file_url);
        const reader = new FileReader();
        reader.onload = function(e) {
          const arrayBuffer = e.target.result;
          font = opentype.parse(arrayBuffer);
          console.log("GlyphsViewer - loaded uploaded font")
          getAllGlyphs()
        }
        reader.readAsArrayBuffer(file);
      } catch (err) {
        console.error("Failed to load uploaded font:", err);
      }
    }
    
    else {
      if(!$local_fonts && !skip) return console.log("You need local fonts permission for the Glyphs Viewer")
      try {
        const fonts = await window.queryLocalFonts({
          postscriptNames: [font_obj.postscript_name],
        });
        for (const fontData of fonts) {
          let sfnt = await fontData.blob()
          let sfntArrayBuffer = await sfnt.arrayBuffer()
          font = opentype.parse(sfntArrayBuffer)
          console.log("GlyphsViewer - loaded local font")
          getAllGlyphs()
        }
      } catch (err) {
        console.error(err.name, err.message);
      }
    }
  }

  function getAllGlyphs() {
    let glyphs = font.glyphs.glyphs;
    if (!glyphs || glyphs.length === 0) {
      console.log("GlyphsViewer - no glyphs found in this font");
      return;
    }

    for (const index in Object.entries(glyphs)) {
      let glyph = glyphs[index]
      if(glyph.unicode && glyph.name) {
        let obj =  {name: "", unicode: "" }
        obj.name = glyph.name
        obj.unicode = "&#"+glyph.unicode+";"
        all_glyphs = [...all_glyphs, obj]
      }
    }
    findAlternativeGlyphs()
    if (!$user_valid) {
      all_glyphs = sampleSize(all_glyphs, 60)
    }
  }

  function findAlternativeGlyphs() {
    // filter down all glyphs to the ones that start with the same letter
    alternative_glyphs = all_glyphs.filter(glyph => $text_entered.toLowerCase().includes(glyph.name.toLowerCase().charAt(0)))
    if (!$user_valid) {
      alternative_glyphs = sampleSize(alternative_glyphs, 30)
    }
    glyphs_loaded = true
  }

  function toggleCharacterMap() {
    if (view_option !== "none") {
      is_visible = true
    }else {
      is_visible = false
    }
  }

  function scrollIntoView() {
    if (window.scrollY < 150) {
      window.setTimeout(() => window.scrollTo(0, 150), 100)
    }
  }

  const copyToClipboard = async (glyph) => {
    navigator.clipboard.writeText(glyph).then(
      () => {
        copyToClipboardSuccess = true
        // setTimeout(() => {
        //   copyToClipboardSuccess = false
        // }, 2000)
      },
      () => {
        console.log("cannot copy to clipboard")
      }
    );
    window.scrollTo(0, 45);

    $flash_content = { message: $_('flashes.copiedCharacter'), centered: !$flash_shown_copy_paste }
    if ($flash_shown_copy_paste) return
    window.setTimeout(() => $flash_content = { message: $_('flashes.pasteCharacter', {values: {keys: keys}}), bright: true, centered: true }, 4000)
    $flash_shown_copy_paste = true
  };
</script>

<div class="GlyphsViewer" bind:this={element} class:is_visible>
  <div class="toggle" on:click={scrollIntoView}>
    {#if glyphs_loaded}
      <HorizontalOptionsList list_options={view_options} bind:selected_option={view_option}/>
    {:else}
    <Blink>
      <span>
        Loading all characters...
      </span>
    </Blink>
    {/if}
  </div>
  {#if view_option !== "none"}
    <div class="content">
      <ul class="list" style="font-family: {'"'+font_obj.postscript_name+'"'}, {'"'+font_obj.family_name+'"'}; ">
        {#each glyphs_to_show as glyph}
          <li on:click={copyToClipboard(decode(glyph.unicode))}>
            {@html glyph.unicode}
            {#if !!glyph.name}
              <span class="name">{glyph.name}</span>
            {/if}
          </li>
        {/each}
      </ul>

      {#if !$previews_are_google_fonts && !$local_fonts && supports_local_font_access}
        <div class="remarks">
          <p>With your permission Wordmark can now show all the characters inside this font<br>enabling you to explore and use alternate characters and ligatures.</p>
          <a href="/help/font-detection/" target="_blank"> ↗︎ Read how you can allow your browser to show your fonts on Wordmark</a>
        </div>

      {:else if !$previews_are_google_fonts && !$local_fonts && !supports_local_font_access}
        <div class="remarks">
          <p>Wordmark can show all the characters inside this font on Google Chrome or Microsoft Edge<br>enabling you to explore and use alternate characters and ligatures.</p>
          <a href="/help/font-detection/" target="_blank"> ↗︎ You can read how Wordmark shows your fonts here</a>
        </div>

      {:else if !$user_valid}
        <Insert insert_position={"filtered_previews"} persistent>
          <span slot="toptitle">
            { $_('inserts.map.topTitle') }
          </span>
          <span slot="title">
            { $_('inserts.map.title') }
          </span>
          <span slot="text">
            { $_('inserts.map.text') }
          </span>
          <span slot="button">
            <OpenBuyModalButton feature="map">{@html $_('buttons.upgrade') }</OpenBuyModalButton> 
            <br>
            <br>
            <small>
              <a href="/help/character-map/" target="_blank"> ↗︎ Read more about how this feature helps</a>
            </small>
          </span>
        </Insert>
      {:else if glyphs_to_show.length === 0}
        <div class="remarks">
          Could not find any alternative characters
        </div>
      {/if}
    </div>
  {/if}
</div>

<style>
  .GlyphsViewer {
    position: fixed;
    z-index: 4;
    bottom: 0;
    width: 100%;
    margin-left: -1rem;
    scroll-behavior: smooth;
  }
  .GlyphsViewer.is_visible {
    position: absolute;
    bottom: inherit;
    top: calc(100vh - 24rem);
  }
  
  .GlyphsViewer .toggle {
    display: inline-block;
    position: sticky;
    z-index: 3;
    top: 0.9rem;
    padding: 0.3rem 1.5rem 0.7rem;
    line-height: 3rem;
    font-size: 1.6rem;
    background-color: var(--c-bg);
    border-top-left-radius: 2rem;
    border-top-right-radius: 2rem;
  }
  .GlyphsViewer .toggle:hover {
    color: var(--c-text-on-fill-hover);
  }
  .light_theme .GlyphsViewer.is_visible .toggle {
    border-top: 2px solid var(--c-pill-bg);
  }
  .dark_theme .GlyphsViewer.is_visible .toggle {
    border-top: 2px solid var(--c-flash-bg);
  }

  .GlyphsViewer .content {
    background: linear-gradient(to bottom, var(--c-bg) 0%, var(--c-bg-a2) 5%);
    background-color: var(--c-bg-a2);
  }
  
  .GlyphsViewer .content .remarks {
    font-size: 1.6rem;
    padding-top: 2rem;
    padding-bottom: 3rem;
  }
  .GlyphsViewer .content .remarks p {
    margin-bottom: 2rem;
  }

  .GlyphsViewer .list {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-evenly;
    padding: 0px 2rem;
    margin-top: -0.2rem;
  }
  .GlyphsViewer .list li {
    position: relative;
    width: 10rem;
    height: 10rem;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    font-size: 4rem;
    cursor: pointer;
  }
  .light_theme .GlyphsViewer .list {
    border-top: 2px solid var(--c-pill-bg);
  }
  .dark_theme .GlyphsViewer .list {
    border-top: 2px solid var(--c-flash-bg);
  }

  .GlyphsViewer .list li .name {
    display: none;
    position: absolute;
    bottom: 0.2rem;
    width: 100%;
    font-size: 0.9rem;
    line-height: 1.2;
    font-family: var(--PensumSans);
    background-color: var(--c-bg);
    padding: 0.2rem 0.6rem 0.4rem;
  }

  .GlyphsViewer .list li:hover {
    background-color: var(--c-bg);
    z-index: 1;
    transform: scale(1.5);
    box-shadow: var(--box-shadow);
  }
  .GlyphsViewer .list li:hover .name{
    display: inline;
  }

  .GlyphsViewer .list li::before {
    content: "click to copy";
    position: absolute;
    z-index: 1;
    bottom: 0.2rem;
    width: 100%;
    white-space: nowrap;
    opacity: 0;
    font-size: 0.9rem;
    line-height: 1.2;
    font-family: var(--PensumSans);
    background-color: var(--c-bg);
    padding: 0.2rem 0.6rem 0.4rem;
  }
  .GlyphsViewer .list li:hover::before {
    animation-name: fadeInOut;
    animation-duration: 2s;
    animation-delay: 0.75s;
    animation-timing-function: var(--transition-function);
  }

  @keyframes fadeInOut {
    0%   { opacity:0; }
    25%  { opacity:1; }
    75%  { opacity:1; }
    100% { opacity:0; }
  }

</style>